import React, { useEffect, useRef, useState } from 'react';
import { Check, Eye, Mail, MailOpen, MoreHorizontal } from 'lucide-react';
import { compactRelativeTime, inboxTypeLabel, notificationTypeGroup } from '../../utils/notificationInbox';
import './NotificationRow.css';

export interface NotificationRowData {
  id: string;
  type?: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  isPriority?: boolean;
  projectId?: string | null;
}

interface NotificationRowProps {
  notification: NotificationRowData;
  onClick: () => void;
  clickable?: boolean;
  /** Preview does not mark read; stopPropagation is handled here. */
  onPreview?: () => void;
  showPreview?: boolean;
  onToggleRead?: () => void;
  selected?: boolean;
  onToggleSelect?: () => void;
  showSelect?: boolean;
  onMuteProject?: () => void;
  onMentionsOnly?: () => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  onClick,
  clickable = true,
  onPreview,
  showPreview = false,
  onToggleRead,
  selected = false,
  onToggleSelect,
  showSelect = false,
  onMuteProject,
  onMentionsOnly,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hasProjectActions = !!(notification.projectId && (onMuteProject || onMentionsOnly));

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const classes = [
    'notif-row',
    notification.read ? 'is-read' : 'is-unread',
    notification.isPriority ? 'is-priority' : '',
    clickable ? 'is-clickable' : '',
    showPreview || onToggleRead || hasProjectActions || showSelect ? 'has-preview' : '',
    selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {showSelect && onToggleSelect ? (
        <label className="notif-row-select" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Select ${notification.title}`}
          />
        </label>
      ) : null}
      <button
        type="button"
        className="notif-row-main"
        onClick={onClick}
        aria-label={`${notification.title}. ${notification.message}`}
        disabled={!clickable}
      >
        <span className="notif-row-dot" aria-hidden="true" />
        <span className="notif-row-copy">
          <span className="notif-row-title">{notification.title}</span>
          {notification.message ? (
            <span className="notif-row-message"> — {notification.message}</span>
          ) : null}
        </span>
        <span className={`notif-row-type notif-type-${notificationTypeGroup(notification.type) || 'note'}`}>
          {inboxTypeLabel(notification.type)}
        </span>
        <span className="notif-row-time">{compactRelativeTime(notification.timestamp)}</span>
      </button>
      <div className="notif-row-actions">
        {onToggleRead ? (
          <button
            type="button"
            className="notif-row-preview"
            aria-label={notification.read ? `Mark ${notification.title} unread` : `Mark ${notification.title} read`}
            title={notification.read ? 'Mark unread' : 'Mark read'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleRead();
            }}
          >
            {notification.read ? <Mail size={14} aria-hidden /> : <MailOpen size={14} aria-hidden />}
          </button>
        ) : null}
        {showPreview && onPreview ? (
          <button
            type="button"
            className="notif-row-preview"
            aria-label={`Preview ${notification.title}`}
            title="Preview"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPreview();
            }}
          >
            <Eye size={14} aria-hidden />
          </button>
        ) : null}
        {hasProjectActions ? (
          <div className="notif-row-more" ref={menuRef}>
            <button
              type="button"
              className="notif-row-preview"
              aria-label="Notification actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="More"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
            >
              <MoreHorizontal size={14} aria-hidden />
            </button>
            {menuOpen ? (
              <div className="notif-row-more-menu" role="menu">
                {onMentionsOnly ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onMentionsOnly();
                    }}
                  >
                    Mentions only
                  </button>
                ) : null}
                {onMuteProject ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onMuteProject();
                    }}
                  >
                    Mute this project
                  </button>
                ) : null}
                {onToggleRead ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onToggleRead();
                    }}
                  >
                    {notification.read ? (
                      <>
                        <Mail size={12} aria-hidden /> Mark unread
                      </>
                    ) : (
                      <>
                        <Check size={12} aria-hidden /> Mark read
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationRow;
